import React, {useState, useEffect} from "react";
import {GoogleMap, DirectionsRenderer, Circle, Marker} from "@react-google-maps/api";
import {useJsApiLoader} from "@react-google-maps/api";
import {libraries} from "../../../utility/Utils";
import config from "../../../config/env/config";

interface MapComponentProps {
    startLat: number;
    startLon: number;
    endLat: number;
    endLon: number;
    circle?: boolean;
    circleStartRadius?: number;
    circleEndRadius?: number;
    height?: string;
}

const MapComponent: React.FC<MapComponentProps> = ({
                                                       startLat,
                                                       startLon,
                                                       endLat,
                                                       endLon,
                                                       circle,
                                                       circleStartRadius,
                                                       circleEndRadius,
                                                       height = "255px"
                                                   }) => {
    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

    const {isLoaded, loadError} = useJsApiLoader({
        googleMapsApiKey: config.googleMapsApiKey,
        libraries,
    });

    const mapContainerStyle = {
        width: "100%",
        height: height,
        borderRadius: "10px",
    };
    const zuppinMapStyle: google.maps.MapTypeStyle[] = [
        { elementType: "geometry", stylers: [{ color: "#f8fafc" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#0f172a" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#f8fafc" }] },

        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "poi.business", stylers: [{ visibility: "off" }] },

        { featureType: "road", elementType: "geometry", stylers: [{ color: "#e2e8f0" }] },
        { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#cbd5e1" }] },
        { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#334155" }] },

        { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
        { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#93c5fd" }] },

        { featureType: "transit", stylers: [{ visibility: "off" }] },

        { featureType: "water", elementType: "geometry", stylers: [{ color: "#e0f2fe" }] },
        { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0f172a" }] },

        { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#cbd5e1" }] },
        { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#0f172a" }] },
    ];
    const mapOptions: google.maps.MapOptions = {
        styles: zuppinMapStyle,
        disableDefaultUI: true,
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        gestureHandling: "greedy",
        tilt: 0,
    };

    const circles = [
        {
            id: 1,
            center: {lat: startLat || 39.9334, lng: startLon || 32.8597},
            radius: circleStartRadius ?? 0,
        },
        {
            id: 2,
            center: {lat: endLat || 39.9334, lng: endLon || 32.8597},
            radius: circleEndRadius ?? 0,
        },
    ];

    const fetchDirections = () => {
        if (!isLoaded || !window.google) {
            console.error("Google Maps API not loaded.");
            return;
        }

        const directionsService = new google.maps.DirectionsService();
        directionsService.route(
            {
                origin: {lat: startLat, lng: startLon},
                destination: {lat: endLat, lng: endLon},
                travelMode: google.maps.TravelMode.DRIVING,
                avoidTolls: true
            },
            (result, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                    setDirections(result);
                } else {
                    console.error(`Error fetching directions: ${status}`);
                }
            }
        );
    };

    useEffect(() => {
        if (isLoaded) {
            fetchDirections();
        }
    }, [isLoaded, startLat, startLon, endLat, endLon]);

    if (loadError) {
        return <div>Error loading Google Maps API</div>;
    }

    if (!isLoaded) {
        return <div>Loading Google Maps...</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={{
                lat: (startLat + (endLat || startLat)) / 2,
                lng: (startLon + (endLon || startLon)) / 2,
            }}
            zoom={8}
            options={mapOptions}
        >
            {directions && <DirectionsRenderer directions={directions}/>}
            {circle &&
                circles.map((circle) => (
                    <Circle
                        key={`circle-${circle.id}`}
                        center={circle.center}
                        radius={circle.radius}
                        options={{
                            fillColor: "rgba(0, 123, 255, 0.4)",
                            strokeColor: "blue",
                            strokeWeight: 2,
                        }}
                    />
                ))}
            {!endLat && !endLon && (
                <Marker
                    position={{lat: startLat, lng: startLon}}
                    title="Başlangıç Noktası"
                />
            )}
        </GoogleMap>
    );
};

export default MapComponent;
